// src/components/pages/PrivacyPolicy.js
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import CookieSettings from '../common/CookieSettings';

function PrivacyPolicy() {
  const { language } = useLanguage();
  const location = useLocation();
  
  // Refs für die Abschnitte
  const cookieSettingsRef = useRef(null);
  const topRef = useRef(null);
  
  // Datenschutzinhalt je nach Sprache
  const privacyContent = {
    en: {
      title: "Privacy Policy",
      lastUpdated: "Last Updated: May 2025",
      cookieSettings: "Cookie Settings",
      cookieInfo: "You can adjust your cookie preferences below. We only use cookies for analytics purposes after your explicit consent.",
      sections: [
        {
          title: "1. Introduction",
          content: "Lucent Web Design ('we', 'our', or 'us') respects your privacy and is committed to protecting your personal data. This Privacy Policy will inform you about how we look after your personal data when you visit our website and tell you about your privacy rights and how the law protects you."
        },
        {
          title: "2. Data Collection",
          content: "We collect anonymous usage data using Google Analytics to improve our website's performance and user experience. This includes information about your browser, device, location, and how you interact with our site. This data is collected only after you have given your explicit consent through our cookie banner."
        },
        {
          title: "3. Cookies",
          content: "We use cookies to enable analytics tracking. You can opt out by declining cookies in the consent banner or by adjusting your browser settings. We store your cookie preferences for 150 days. If you accept cookies, we will use them to collect anonymous analytics data. No cookies are set if you decline."
        },
        {
          title: "4. Google Analytics",
          content: "This website uses Google Analytics to collect anonymous usage statistics. Google Analytics uses cookies to help us analyze how users use our site. The information generated about your use of the website (including your IP address) will be transmitted to and stored by Google on servers in the United States. We have activated IP anonymization so your IP address will be truncated within EU/EEA member states. Only in exceptional cases will the full IP address be transmitted to Google servers in the USA and truncated there. Google will use this information for the purpose of evaluating your use of the website, compiling reports on website activity for website operators, and providing other services relating to website activity and internet usage. Google may also transfer this information to third parties where required to do so by law, or where such third parties process the information on Google's behalf."
        },
        {
          title: "5. Your Rights",
          content: "Under GDPR, you have the right to access, rectify, port and erase your data. You have the right to restrict or object to our processing of your data. To exercise these rights, please contact us. You also have the right to withdraw your consent at any time by declining cookies in our cookie banner, which will disable all tracking."
        },
        {
          title: "6. Contact Information",
          content: "If you have any questions about this Privacy Policy or our data practices, please contact us at privacy@lucentwebdesign.com."
        }
      ]
    },
    de: {
      title: "Datenschutzerklärung",
      lastUpdated: "Zuletzt aktualisiert: Mai 2025",
      cookieSettings: "Cookie-Einstellungen",
      cookieInfo: "Sie können Ihre Cookie-Einstellungen unten anpassen. Wir verwenden Cookies für Analysezwecke nur nach Ihrer ausdrücklichen Einwilligung.",
      sections: [
        {
          title: "1. Einleitung",
          content: "Lucent Web Design ('wir', 'unser' oder 'uns') respektiert Ihre Privatsphäre und verpflichtet sich, Ihre persönlichen Daten zu schützen. Diese Datenschutzerklärung informiert Sie darüber, wie wir Ihre persönlichen Daten behandeln, wenn Sie unsere Website besuchen, und informiert Sie über Ihre Datenschutzrechte und darüber, wie das Gesetz Sie schützt."
        },
        {
          title: "2. Datenerhebung",
          content: "Wir sammeln anonyme Nutzungsdaten mit Google Analytics, um die Leistung und Benutzererfahrung unserer Website zu verbessern. Dies umfasst Informationen über Ihren Browser, Ihr Gerät, Ihren Standort und Ihre Interaktion mit unserer Website. Diese Daten werden nur erfasst, nachdem Sie Ihre ausdrückliche Einwilligung über unser Cookie-Banner gegeben haben."
        },
        {
          title: "3. Cookies",
          content: "Wir verwenden Cookies, um das Analytics-Tracking zu ermöglichen. Sie können dies ablehnen, indem Sie Cookies im Consent-Banner ablehnen oder Ihre Browsereinstellungen anpassen. Wir speichern Ihre Cookie-Präferenzen für 150 Tage. Wenn Sie Cookies akzeptieren, werden wir sie verwenden, um anonyme Analysedaten zu sammeln. Es werden keine Cookies gesetzt, wenn Sie ablehnen."
        },
        {
          title: "4. Google Analytics",
          content: "Diese Website verwendet Google Analytics, um anonyme Nutzungsstatistiken zu sammeln. Google Analytics verwendet Cookies, um uns bei der Analyse der Nutzung unserer Website zu helfen. Die durch die Nutzung der Website erzeugten Informationen (einschließlich Ihrer IP-Adresse) werden an Server von Google in den USA übertragen und dort gespeichert. Wir haben die IP-Anonymisierung aktiviert, sodass Ihre IP-Adresse innerhalb der Mitgliedstaaten der EU/des EWR gekürzt wird. Nur in Ausnahmefällen wird die volle IP-Adresse an Google-Server in den USA übertragen und dort gekürzt. Google wird diese Informationen nutzen, um Ihre Nutzung der Website auszuwerten, Berichte über die Website-Aktivitäten für die Website-Betreiber zusammenzustellen und weitere mit der Website-Nutzung und der Internetnutzung verbundene Dienstleistungen zu erbringen. Google kann diese Informationen auch an Dritte übertragen, sofern dies gesetzlich vorgeschrieben ist oder soweit Dritte diese Daten im Auftrag von Google verarbeiten."
        },
        {
          title: "5. Ihre Rechte",
          content: "Gemäß DSGVO haben Sie das Recht auf Zugang, Berichtigung, Übertragbarkeit und Löschung Ihrer Daten. Sie haben das Recht, die Verarbeitung Ihrer Daten einzuschränken oder ihr zu widersprechen. Um diese Rechte auszuüben, kontaktieren Sie uns bitte. Sie haben auch das Recht, Ihre Einwilligung jederzeit zu widerrufen, indem Sie Cookies in unserem Cookie-Banner ablehnen, wodurch jegliches Tracking deaktiviert wird."
        },
        {
          title: "6. Kontaktinformationen",
          content: "Wenn Sie Fragen zu dieser Datenschutzerklärung oder unseren Datenpraktiken haben, kontaktieren Sie uns bitte unter datenschutz@lucentwebdesign.com."
        }
      ]
    }
  };
  
  const content = language === 'de' ? privacyContent.de : privacyContent.en;

  // Update document title
  useEffect(() => {
    document.title = `${content.title} | Lucent Web Design`;
  }, [content.title]);
  
  // Scroll zu den entsprechenden Abschnitten beim Laden der Seite
  useEffect(() => {
    // Zeit geben, damit die Komponente vollständig gerendert wird
    const timer = setTimeout(() => {
      if (location.hash === '#cookie-settings-section' && cookieSettingsRef.current) {
        // Scroll zum Cookie-Settings-Bereich
        cookieSettingsRef.current.scrollIntoView({ behavior: 'smooth' });
      } else {
        // Wenn kein Hash oder ungültiger Hash, scroll zum Seitenanfang
        window.scrollTo(0, 0);
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location]);

  return (
    <section className="privacy-policy" style={{ padding: '80px 0', color: 'var(--text-light)' }}>
      <div className="container" ref={topRef}>
        <h1 style={{ marginBottom: '20px', color: 'var(--text-light)' }}>{content.title}</h1>
        <p className="last-updated" style={{ marginBottom: '40px', color: '#858585', fontStyle: 'italic' }}>
          {content.lastUpdated}
        </p>
        
        {/* Cookie Settings Section */}
        <div id="cookie-settings-section" ref={cookieSettingsRef}>
          <h2 style={{ fontSize: '1.8rem', marginBottom: '15px', color: 'var(--primary)' }}>
            {content.cookieSettings}
          </h2>
          <p style={{ lineHeight: '1.7', marginBottom: '15px' }}>{content.cookieInfo}</p>
          
          {/* Include Cookie Settings Component */}
          <CookieSettings />
        </div>
        
        {/* Privacy Policy Sections */}
        {content.sections.map((section, index) => (
          <div key={index} className="policy-section" style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '15px', color: 'var(--primary)' }}>
              {section.title}
            </h2>
            <p style={{ lineHeight: '1.7', marginBottom: '15px' }}>{section.content}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default PrivacyPolicy;